/*
 * pw-geo-gate.js — International visitor gate.
 *
 * Detects visitor country silently. If outside the allowed list, blocks all
 * commerce actions (add-to-cart, checkout, AI generate) and — ONLY when the
 * visitor actually clicks one of those actions — opens a modal explaining
 * that we ship internationally by quote, with a link to the enquiry page.
 * Nothing appears on page load, so it never competes with marketing popups.
 *
 * Config is rendered by snippets/pw-geo-gate.liquid on <html> data attrs and
 * on the <dialog id="pw-geo-dialog"> element:
 *   data-pw-geo-enabled          "true"/"false"     kill switch
 *   data-pw-geo-allowed          "AU,NZ"            comma-separated ISO-2
 *   data-pw-geo-shop-country     "AU"               localization.country.iso_code
 *   data-pw-geo-trust-customer   "true"             logged-in AU customer → skip
 */
(() => {
  'use strict';

  const html = document.documentElement;
  const enabled = (html.getAttribute('data-pw-geo-enabled') || '').toLowerCase() === 'true';
  if (!enabled) return;

  const allowedRaw = html.getAttribute('data-pw-geo-allowed') || '';
  const allowed = allowedRaw
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  if (!allowed.length) return; // no allowlist → gate disabled

  const trustCustomer = (html.getAttribute('data-pw-geo-trust-customer') || '') === 'true';
  const shopCountry = (html.getAttribute('data-pw-geo-shop-country') || '').toUpperCase();

  const SESSION_KEY = 'pw_geo_country';
  const FORCE_PARAM = 'pw_force_country';

  const log = (...a) => { try { console.debug('[pw-geo]', ...a); } catch (_) {} };

  // -------- resolveCountry ------------------------------------------------
  function readForceParam() {
    try {
      const u = new URL(window.location.href);
      const v = u.searchParams.get(FORCE_PARAM);
      return v ? v.trim().toUpperCase() : '';
    } catch (_) { return ''; }
  }

  function readCached() {
    try { return (sessionStorage.getItem(SESSION_KEY) || '').toUpperCase(); }
    catch (_) { return ''; }
  }
  function writeCached(iso) {
    try { sessionStorage.setItem(SESSION_KEY, iso); } catch (_) {}
  }

  async function fetchWithTimeout(url, ms) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    try {
      const r = await fetch(url, { credentials: 'omit', signal: ctrl.signal });
      if (!r.ok) return null;
      return await r.json();
    } catch (_) {
      return null;
    } finally {
      clearTimeout(t);
    }
  }

  async function detectShopify() {
    // Shopify's own endpoint — same one that powers their Geolocation app.
    const j = await fetchWithTimeout('/browsing_context_suggestions.json', 1800);
    const iso =
      j &&
      j.detected_values &&
      j.detected_values.country &&
      (j.detected_values.country.handle || j.detected_values.country.iso_code);
    return iso ? String(iso).toUpperCase() : '';
  }

  async function detectIp() {
    // Free fallback; non-fatal if it fails or is rate-limited.
    const j = await fetchWithTimeout('https://ipapi.co/json/', 1500);
    return j && j.country ? String(j.country).toUpperCase() : '';
  }

  async function resolveCountry() {
    const forced = readForceParam();
    if (forced) { writeCached(forced); return forced; }

    const cached = readCached();
    if (cached) return cached;

    if (trustCustomer && shopCountry) { writeCached(shopCountry); return shopCountry; }

    const a = await detectShopify();
    if (a) { writeCached(a); return a; }

    const b = await detectIp();
    if (b) { writeCached(b); return b; }

    return ''; // unknown → treat as allowed (fail-open), don't gate on network errors
  }

  // -------- popup ---------------------------------------------------------
  const dialog = document.getElementById('pw-geo-dialog');

  function openPopup(reason) {
    if (!dialog) return;
    try {
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      else dialog.setAttribute('open', '');
    } catch (_) {
      dialog.setAttribute('open', '');
    }
    dialog.setAttribute('data-pw-reason', reason || 'auto');
  }
  function closePopup() {
    if (!dialog) return;
    try { if (typeof dialog.close === 'function') dialog.close(); }
    catch (_) { dialog.removeAttribute('open'); }
  }

  function wireDialog() {
    if (!dialog) return;
    dialog.addEventListener('click', (ev) => {
      const t = ev.target;
      if (!(t instanceof Element)) return;
      const act = t.closest('[data-pw-geo-action]');
      if (act && act.getAttribute('data-pw-geo-action') === 'dismiss') {
        closePopup();
        return;
      }
      // Backdrop click closes.
      if (ev.target === dialog) closePopup();
      // "inquire" is a plain <a href> — let the browser navigate.
    });
  }

  // -------- block enforcement --------------------------------------------
  function applyBlock() {
    html.setAttribute('data-pw-geo-blocked', 'true');
  }

  function isBlocked() {
    return html.getAttribute('data-pw-geo-blocked') === 'true';
  }

  function shouldInterceptTarget(target) {
    if (!(target instanceof Element)) return false;
    // Design Studio buttons are handled inside design-studio.js directly, but
    // we still gate the click here as belt-and-braces for any duplicate paths.
    if (target.closest('[data-action="add-to-cart"]')) return { kind: 'studio-add' };
    if (target.closest('[data-action="ai-generate"]')) return { kind: 'studio-generate' };
    // Regular PDP / cart forms.
    const submitBtn = target.closest('button[type="submit"], input[type="submit"], a[href*="/checkout"]');
    if (submitBtn) {
      const form = submitBtn.closest('form');
      if (form) {
        const action = (form.getAttribute('action') || '').toLowerCase();
        if (action.includes('/cart/add')) return { kind: 'cart-add' };
        if (action.includes('/checkout') || action.includes('/cart')) {
          // Checkout button inside cart form.
          const name = (submitBtn.getAttribute('name') || '').toLowerCase();
          if (name === 'checkout' || action.includes('/checkout')) return { kind: 'checkout' };
        }
      }
      if (submitBtn.tagName === 'A') {
        const href = (submitBtn.getAttribute('href') || '').toLowerCase();
        if (href.includes('/checkout')) return { kind: 'checkout' };
      }
    }
    return false;
  }

  function installInterceptors() {
    // Click capture — stop the submit/navigation BEFORE any handler runs.
    document.addEventListener('click', (ev) => {
      if (!isBlocked()) return;
      const hit = shouldInterceptTarget(ev.target);
      if (!hit) return;
      ev.preventDefault();
      ev.stopPropagation();
      openPopup(hit.kind);
    }, true);

    // Form submit capture — in case a form is submitted programmatically.
    document.addEventListener('submit', (ev) => {
      if (!isBlocked()) return;
      const form = ev.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = (form.getAttribute('action') || '').toLowerCase();
      if (action.includes('/cart/add') || action.includes('/checkout')) {
        ev.preventDefault();
        ev.stopPropagation();
        openPopup(action.includes('/checkout') ? 'checkout' : 'cart-add');
      }
    }, true);
  }

  // -------- boot ----------------------------------------------------------
  wireDialog();

  resolveCountry().then((iso) => {
    log('country =', iso, 'allowed =', allowed);
    if (!iso) return; // unknown, fail-open
    if (allowed.indexOf(iso) !== -1) return; // allowed

    // On-action only: apply block + install interceptors, but do NOT open the
    // popup on load. The dialog opens only when the visitor clicks a blocked
    // action (add-to-cart, ai-generate, checkout).
    applyBlock();
    installInterceptors();
  });
})();
